import { ChangeDetectionStrategy, Component, TemplateRef, inject, input } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FilterGroupDef, FilterRailManifest } from './filter-manifest.types';
import { ProductRailGroupComponent } from './groups/product-rail-group.component';
import { ProductChecklistPickerComponent } from './pickers/product-checklist-picker.component';
import { ProductPremiumInputComponent } from './pickers/product-premium-input.component';
import { ProductsFilterStateService, formatPremium } from '../state/products-filter-state.service';
import { summarizeMulti } from '../../rates/state/dim-summarizers';

/**
 * Architectural prototype (2026-06-11) — generic, manifest-driven Advanced
 * filters. Renders any FilterRailManifest's groups → dims, choosing the picker
 * by dim.kind. This ONE component replaces the hand-written, per-surface group
 * HTML; a new surface = a new manifest, not a new component.
 *
 * - checklist / single → <app-product-checklist-picker> (already shared chrome).
 * - slot               → host-projected <ng-template> (carrier fly-out, status
 *                        toggle) looked up by name in [slots].
 *
 * Group active-count (auto-expand) + value-summary badge are computed generically
 * from ProductsFilterStateService — proving the badge logic also drops out of the
 * hand-written rail. Wired to the products state service for the prototype; the
 * production version would take a surface-agnostic FilterStore.
 */
@Component({
  selector: 'app-filter-rail-groups',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, ProductRailGroupComponent, ProductChecklistPickerComponent, ProductPremiumInputComponent],
  template: `
    @for (g of manifest().groups; track g.id) {
      <app-product-rail-group [name]="g.label" [activeCount]="groupActive(g)" [badge]="groupBadge(g)">
        @for (d of g.dims; track d.id) {
          @switch (d.kind) {
            <!-- headless on a one-dimension group, matching the hand-written
                 rail: the picker's head row would be a second accordion opening
                 the one thing the group header already names, repeating its
                 summary a line below. Derived from the manifest rather than
                 declared per dim, so a group that later gains a second dim gets
                 its head rows back on its own. -->
            @case ('checklist') {
              <app-product-checklist-picker [field]="$any(d.field)" [label]="d.label" mode="multi"
                [headless]="g.dims.length === 1"
                [options]="optionsFor()(d.optionsKey ?? '')" [labelMap]="d.labelMap ?? {}" />
            }
            @case ('single') {
              <app-product-checklist-picker [field]="$any(d.field)" [label]="d.label" mode="single"
                [headless]="g.dims.length === 1"
                [options]="optionsFor()(d.optionsKey ?? '')" [labelMap]="d.labelMap ?? {}" />
            }
            @case ('premium') {
              <app-product-premium-input [field]="$any(d.field)" [label]="d.label" />
            }
            @case ('slot') {
              @if (slots()[d.slot ?? '']; as tpl) {
                <ng-container [ngTemplateOutlet]="tpl" />
              }
            }
          }
        }
      </app-product-rail-group>
    }
  `,
})
export class FilterRailGroupsComponent {
  /** The surface manifest to render. */
  readonly manifest = input.required<FilterRailManifest>();
  /** Host-provided option-list lookup (key → string[]). */
  readonly optionsFor = input.required<(key: string) => string[]>();
  /** Host-projected templates for 'slot' dims, keyed by dim.slot. */
  readonly slots = input<Record<string, TemplateRef<unknown> | undefined>>({});

  private readonly fs = inject(ProductsFilterStateService);

  /** Distinct active dims in a group → drives the group's auto-expand. */
  protected groupActive(g: FilterGroupDef): number {
    this.fs.version();
    let n = 0;
    for (const d of g.dims) if (this.dimActive(d)) n++;
    return n;
  }

  /** Generic value summary for the group header badge ("All" when empty). */
  protected groupBadge(g: FilterGroupDef): string {
    this.fs.version();
    const parts: string[] = [];
    for (const d of g.dims) {
      const s = this.dimSummary(d);
      if (s) parts.push(s);
    }
    return parts.length ? parts.join(' · ') : 'All';
  }

  private slotVal(field?: string): unknown {
    return field ? (this.fs.slot as unknown as Record<string, unknown>)[field] : undefined;
  }

  private dimActive(d: { kind: string; field?: string; slot?: string }): boolean {
    if (d.kind === 'slot') {
      if (d.slot === 'carrier') return (this.fs.slot.companyFilter?.companies?.length ?? 0) > 0;
      if (d.slot === 'status') return this.fs.slot.filterStatus !== 'all';
      return false;
    }
    const v = this.slotVal(d.field);
    if (d.kind === 'premium') return v != null;
    return Array.isArray(v) ? v.length > 0 : !!v;
  }

  private dimSummary(d: { kind: string; field?: string; slot?: string }): string {
    if (d.kind === 'slot') {
      if (d.slot === 'carrier') return summarizeMulti(this.fs.slot.companyFilter?.companies ?? []);
      if (d.slot === 'status') {
        const s = this.fs.slot.filterStatus;
        return s !== 'all' ? s[0].toUpperCase() + s.slice(1) : '';
      }
      return '';
    }
    const v = this.slotVal(d.field);
    // Premium dims hold a number — format as currency so the group badge reads
    // "$25K" rather than a raw 25000.
    if (d.kind === 'premium') {
      return v == null ? '' : formatPremium(v as number);
    }
    if (Array.isArray(v)) return summarizeMulti(v as string[]);
    return (v as string) || '';
  }
}
